import { FC, KeyboardEvent, useState } from 'react';

import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { ResaleListingGroup } from '../../hooks/contexts/resale-listing.context';
import formatNumericValue from '../../utils/formatNumericValue';
import * as Style from './styles';

const HIGH_DEMAND_THRESHOLD = 40;

type ResaleListingAccordeonProps = {
  groups: ResaleListingGroup[];
  onSelectGroup: (group: ResaleListingGroup) => void;
};

type AccordionSection = { groupName: string; rows: ResaleListingGroup[] };

function buildSections(groups: ResaleListingGroup[]): AccordionSection[] {
  const map = new Map<string, AccordionSection>();
  for (const g of groups) {
    if (!map.has(g.groupName)) map.set(g.groupName, { groupName : g.groupName, rows : [] });
    map.get(g.groupName)!.rows.push(g);
  }
  return [...map.values()];
}

const ResaleListingAccordeon: FC<ResaleListingAccordeonProps> = ({ groups, onSelectGroup }) => {
  const { translate } = useI18nContext();

  const sections = buildSections(groups);
  const totalStock = groups.reduce((sum, g) => sum + g.count, 0);
  const isHighDemand = totalStock > HIGH_DEMAND_THRESHOLD;

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = new Set<string>();
    if (sections.length > 0) s.add(sections[0].groupName);
    return s;
  });

  const handleToggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>, name: string) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(name); }
  };

  const handleRowKeyDown = (e: KeyboardEvent<HTMLLIElement>, group: ResaleListingGroup) => {
    if (e.key === 'Enter') onSelectGroup(group);
  };

  return (
    <Style.Container id="resale-listing-accordeon">
      {sections.map((section) => {
        const sectionId = section.groupName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        const isOpen = expanded.has(section.groupName);
        const hasFastPass = section.rows.some((r) => r.offerId !== null);
        const stockBadgeLabel = hasFastPass && totalStock > 0
          ? translate(['RESALE_LISTING', isHighDemand ? 'HIGH_DEMAND_BADGE' : 'LAST_UNITS_BADGE'])
          : null;

        return (
          <Style.Group key={section.groupName} id={`resale-listing-group-${sectionId}`}>
            <Style.GroupHeader
              $opened={isOpen}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={`resale-listing-group-body-${sectionId}`}
              id={`resale-listing-group-header-${sectionId}`}
              onClick={() => handleToggle(section.groupName)}
              onKeyDown={(e) => handleHeaderKeyDown(e, section.groupName)}
            >
              <Style.GroupHeaderLeft>
                <Style.GroupTitleRow>
                  <Style.GroupName id={`resale-listing-group-name-${sectionId}`}>
                    {section.groupName}
                  </Style.GroupName>
                  {stockBadgeLabel && (
                    <Style.StockBadge id={`resale-listing-group-stock-badge-${sectionId}`}>
                      {isHighDemand && <span aria-hidden="true">↗</span>}
                      {stockBadgeLabel}
                    </Style.StockBadge>
                  )}
                </Style.GroupTitleRow>
              </Style.GroupHeaderLeft>
              <Style.CircularArrow $opened={isOpen} aria-hidden="true" id={`resale-listing-group-arrow-${sectionId}`}>
                ▼
              </Style.CircularArrow>
            </Style.GroupHeader>

            <Style.GroupBody
              $opened={isOpen}
              role="region"
              id={`resale-listing-group-body-${sectionId}`}
              aria-labelledby={`resale-listing-group-header-${sectionId}`}
            >
              <Style.ItemList>
                {section.rows.map((row) => {
                  const countLabel = translate(
                    ['RESALE_LISTING', row.count === 1 ? 'AVAILABLE_COUNT' : 'AVAILABLE_COUNT_PLURAL'],
                    { count : row.count }
                  );
                  return (
                    <Style.Item
                      key={row.guestTypeId}
                      role="button"
                      tabIndex={isOpen ? 0 : -1}
                      id={`resale-listing-item-${row.uuid}`}
                      onClick={() => onSelectGroup(row)}
                      onKeyDown={(e) => handleRowKeyDown(e, row)}
                    >
                      <Style.ItemInfo>
                        <Style.ItemName id={`resale-listing-item-name-${row.guestTypeId}`}>
                          {row.ticketName}
                        </Style.ItemName>
                        <Style.ItemCount id={`resale-listing-item-count-${row.guestTypeId}`}>
                          {countLabel}
                        </Style.ItemCount>
                      </Style.ItemInfo>
                      <Style.ItemLeft>
                        <Style.ItemPrice id={`resale-listing-item-price-${row.uuid}`}>
                          {formatNumericValue({ value : row.finalPrice, discount : 0, currencyFormat : 'BRL' })}
                        </Style.ItemPrice>
                        <Style.ItemFees id={`resale-listing-item-fees-${row.uuid}`}>
                          {translate(['RESALE_LISTING', 'FEES'])}
                        </Style.ItemFees>
                      </Style.ItemLeft>
                    </Style.Item>
                  );
                })}
              </Style.ItemList>
            </Style.GroupBody>
          </Style.Group>
        );
      })}
    </Style.Container>
  );
};

export { ResaleListingAccordeon };
